import type { RovoLabsConfirmedTools } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

/**
 * Shape forwarded as the `tools` analytics attribute on tools-execution
 * related events. Mirrors the tools attribute spec at:
 * https://hello.atlassian.net/wiki/spaces/~598965687/pages/6881328891/Tools+attributes+analytics+spec
 *
 * @deprecated FE tools instrumentation is being replaced by backend unified
 * instrumentation: https://hello.atlassian.net/wiki/spaces/DATA/pages/7037248568/Backend+Unified+Instrumentation+for+Skills+Tools+Subagents
 * Kept only to populate the legacy `rovoAgent toolsExecution*` events Data
 * Science still consumes.
 */
export type ToolAnalyticsAttribute = {
	toolId: string;
	/**
	 * Raw tool key (e.g. `confluence-page-create`).
	 */
	toolKey?: string;
	/**
	 * Raw tool source string from the backend (e.g. `'MCP_TOOL'`, `'CONVO_AI'`)
	 * — surfaces where the tool definition originated. Named
	 * `toolDefinitionSource` in the emitted analytics payload per the RFC.
	 */
	toolDefinitionSource: string | null;
};

/**
 * Variant emitted on user-driven `actioned` events — adds the resolution that
 * triggered the event (confirmed vs cancelled vs externally actioned).
 *
 * @deprecated See `ToolAnalyticsAttribute`.
 */
export type ToolActionedAnalyticsAttribute = ToolAnalyticsAttribute & {
	resolutionType: string;
};

const UNKNOWN_TOOL_ID = 'UNKNOWN_TOOL_ID';
const MCP_TOOL_SOURCE = 'MCP_TOOL';

/**
 * MCP tool keys are user-generated content and must not leak into analytics
 * payloads. For all other sources (including a missing `toolSource`) we
 * forward the raw key.
 */
const redactToolKeyForPii = ({
	toolDefinitionSource,
	key,
}: {
	toolDefinitionSource: string | undefined;
	key: string | undefined;
}): string | undefined => (toolDefinitionSource === MCP_TOOL_SOURCE ? undefined : key);

type ToolAttributeInput = {
	toolId: string | undefined;
	toolKey: string | undefined;
	toolDefinitionSource: string | undefined;
};

const buildBaseToolAttribute = ({
	toolId,
	toolKey,
	toolDefinitionSource,
}: ToolAttributeInput): ToolAnalyticsAttribute => ({
	toolId: toolId ?? UNKNOWN_TOOL_ID,
	toolKey: redactToolKeyForPii({ toolDefinitionSource, key: toolKey }),
	toolDefinitionSource: toolDefinitionSource ?? null,
});

/**
 * Builds the `tools` attribute array from a `RovoLabsConfirmedTools` payload —
 * used in the resumption flow `sendMessageConfirmedTools` to populate the
 * deprecated `rovoAgent toolsExecution*` events.
 *
 * @deprecated FE tools instrumentation is being replaced by backend unified
 * instrumentation: https://hello.atlassian.net/wiki/spaces/DATA/pages/7037248568/Backend+Unified+Instrumentation+for+Skills+Tools+Subagents
 */
export const buildToolsAttributeFromConfirmedTools = (
	confirmedTools: RovoLabsConfirmedTools,
): ToolActionedAnalyticsAttribute[] =>
	confirmedTools.tools.map((tool) => ({
		...buildBaseToolAttribute({
			toolId: tool.tool_id,
			toolKey: tool.key,
			toolDefinitionSource: tool.tool_source,
		}),
		resolutionType: tool.resolution?.type ?? 'UNKNOWN_RESOLUTION_TYPE',
	}));
